import { useState } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { FacebookGlyph } from '@/components/platform-glyph';
import { TypeRow } from '@/components/type-row';
import { isEligibleFacebookPage, type FacebookPageOption } from '@/lib/facebook-connect';
import { formatInstagramHandle } from '@/lib/channel-display';

/**
 * AIT-621 Page picker step, shared by the Connect Channel modal and the hosted
 * connect page. Pages the customer can connect come first in a list that scrolls
 * inside the dialog instead of growing past the viewport; Pages we could not
 * get a token for (no direct Page role) or another organization already holds
 * sit behind an expander so a long Page list never buries the real choices.
 */
export function FacebookPagePicker({
  step,
  pages,
  onPick,
}: {
  step: 'loadingPages' | 'picking' | 'submitting';
  pages: FacebookPageOption[];
  onPick: (page: FacebookPageOption) => void;
}) {
  const [showDisabled, setShowDisabled] = useState(false);
  if (step === 'loadingPages') {
    return (
      <div role="status" className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading Pages
      </div>
    );
  }
  if (pages.length === 0) {
    return <p className="text-sm text-muted-foreground">No Facebook Pages found for this account.</p>;
  }
  const eligible = pages.filter(isEligibleFacebookPage);
  const disabled = pages.filter((p) => !isEligibleFacebookPage(p));
  const row = (p: FacebookPageOption) => (
    <TypeRow
      key={p.id}
      name={p.name}
      subtitle={
        p.connected === 'here'
          ? 'Already connected here, reconnects'
          : p.instagram?.username
            ? `Also connects ${formatInstagramHandle(p.instagram.username)}`
            : undefined
      }
      icon={<FacebookGlyph />}
      iconTintClass="bg-[#0866FF]/10"
      disabled={!isEligibleFacebookPage(p) || step === 'submitting'}
      disabledHint={
        p.connected === 'elsewhere'
          ? 'Already connected elsewhere'
          : p.disabledReason === 'no_token'
            ? 'Ask a Page admin to connect this Page'
            : ''
      }
      onClick={() => onPick(p)}
    />
  );
  return (
    <div className="flex flex-col gap-2">
      {eligible.length === 0 && (
        <p className="text-sm text-muted-foreground">None of these Pages can be connected from this account.</p>
      )}
      <div className="no-visible-scrollbar flex max-h-[50vh] flex-col gap-2 overflow-y-auto">{eligible.map(row)}</div>
      {disabled.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowDisabled((v) => !v)}
            aria-expanded={showDisabled}
            className="flex w-full items-center gap-1 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronRight className={`size-4 transition-transform ${showDisabled ? 'rotate-90' : ''}`} />
            Pages you cannot connect ({disabled.length})
          </button>
          <div
            className={`grid transition-[grid-template-rows] duration-200 ease-out ${
              showDisabled ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
            <div className="overflow-hidden">
              <div className="no-visible-scrollbar mt-2 flex max-h-64 flex-col gap-2 overflow-y-auto">
                {disabled.map(row)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
